/*
 * Author: Housefolios
 * Create Time: 2021-11-22 08:46:15
 * Modified by: David Helmick
 * Modified time: 2025-01-27 00:00:00
 * Description: Dialog to delete a property or selected properties - Refactored to use generic DeleteConfirmationDialog
 */

import React from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {IconButton,
  ListItemButton,
  Tooltip,
  Box } from '@mui/material'
import { useMutation } from '@apollo/client/react'
import { DELETE_PROPERTIES } from '../mutation'
import { GET_PROPERTIES, GET_TRASH } from '../queries'
import { GET_PORTFOLIOS } from '@/housefolios-components/Portfolio/queries'
import { GET_ME } from '@/housefolios-components/Session/queries'
import DeleteConfirmationDialog from '@/housefolios-components/Common/DeleteConfirmationDialog'
import { useDeleteConfirmation } from '@/housefolios-components/Common/DeleteConfirmationDialog/useDeleteConfirmation'

// Centralized lodash imports - much cleaner!
import { map } from '@/utils/lodash'
import { GET_PROPERTIES_QUERY_LIMIT } from '@/utils/pagination'

function DeletePropertiesComponent(props) {
  const { bulkActionsNotifications, rightClickMenu, sidebarMenu } =
    props

  const [deleteProperties] = useMutation(DELETE_PROPERTIES)

  const { open, loading, openDialog, closeDialog, handleDelete } =
    useDeleteConfirmation({
      onDelete: async () => {
        await deleteProperties({
          awaitRefetchQueries: true,
          variables: { ids: props.selected },
          refetchQueries: [
            {
              query: GET_PROPERTIES,
              variables: {
                portfolioId: [props.portfolioId],
                limit: GET_PROPERTIES_QUERY_LIMIT,
              },
            },
            {
              query: GET_PROPERTIES,
              variables: {
                portfolioId: map(
                  props.portfolios,
                  (portfolio) => portfolio._id,
                ),
                limit: GET_PROPERTIES_QUERY_LIMIT,
              },
            },
            { query: GET_PORTFOLIOS },
            { query: GET_ME },
            { query: GET_TRASH },
          ],
        })

        if (props.setSelected) props.setSelected([])
      },
      successMessage: 'Selected Properties Deleted',
      errorMessage: 'Failed to Delete Selected Properties',
    })

  return (
    <>
      {bulkActionsNotifications && (
        <Tooltip title="Bulk Delete">
          <IconButton onClick={openDialog} size="large">
            <FontAwesomeIcon
              icon={['far', 'trash-can']}
              style={{ color: 'white' }}
            />
          </IconButton>
        </Tooltip>
      )}

      {rightClickMenu && (
        <ListItemButton onClick={openDialog}>
          <Box sx={{
            width: 16,
            minWidth: 16,
            flexShrink: 0,
            textAlign: "center",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            marginRight: 10,
            fontSize: "1.15rem",
            "& > svg": { width: "1em", height: "1em" },
            ".Mui-selected > &": { opacity: "1 !important" },
          }}>
            <FontAwesomeIcon icon={['fas', 'trash']} />
          </Box>
          <span>Delete Property</span>
        </ListItemButton>
      )}

      {sidebarMenu && (
        <ListItemButton
          onClick={openDialog}
          sx={{
            color: 'error.main',
            width: 'auto',
            gap: 1,
            py: 1,
            px: 1.5,
            minHeight: 40,
            overflow: 'visible',
            justifyContent: 'flex-start',
            alignItems: 'center',
          }}
        >
          <FontAwesomeIcon
            icon={['fas', 'trash']}
            style={{ width: '1rem', flexShrink: 0 }}
          />
          <Box
            component="span"
            sx={{ lineHeight: 1.25, whiteSpace: 'nowrap' }}
          >
            Empty Trash
          </Box>
        </ListItemButton>
      )}

      <DeleteConfirmationDialog
        open={open}
        onClose={closeDialog}
        onConfirm={handleDelete}
        title={
          sidebarMenu
            ? 'Are you sure you want to empty the trash?'
            : 'Delete Properties?'
        }
        confirmationText={
          sidebarMenu
            ? 'All data will be deleted. This action cannot be undone'
            : "Delete these properties. This action can't be undone"
        }
        successMessage="Selected Properties Deleted"
        errorMessage="Failed to Delete Selected Properties"
        loading={loading}
        loadingMessage="Deleting Property Data"
        showSharedWarning={false}
        sharedWarningText=""
        icon="trash-can"
        iconStyle="far"
        cancelButtonText="Cancel"
        deleteButtonText="Delete"
        requireConfirmation={true}
        children={null}
      />
    </>
  )
}

export default DeletePropertiesComponent
